import React from 'react';
import {
  Box,
  Typography,
  Tooltip,
  LinearProgress,
  Divider,
  useTheme,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SearchIcon from '@mui/icons-material/Search';
import HandshakeIcon from '@mui/icons-material/Handshake';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useTranslation } from 'react-i18next';
import { Vehicle } from '../../models/vehicle';
import { VehicleAutoSearch, AutoSearchState } from '../../models/vehicleAutoSearch';
import { VehicleAutoNegoSession, AutoNegoState } from '../../models/vehicleAutoNegoSession';

interface AutomationStatusOverviewProps {
  vehicles: Vehicle[];
  autoSearchSessions: Map<number, VehicleAutoSearch | null>;
  autoNegoSessions: Map<number, VehicleAutoNegoSession | null>;
}

type StatTone = 'neutral' | 'warning' | 'success';

interface StatPillProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  tooltip: string;
  tone?: StatTone;
}

const StatPill: React.FC<StatPillProps> = ({ icon, label, value, tooltip, tone = 'neutral' }) => {
  const theme = useTheme();
  const isZero = value === 0;
  const active = value > 0 && tone !== 'neutral';

  const accentHex =
    tone === 'warning' ? theme.palette.warning.main :
      tone === 'success' ? theme.palette.success.main :
        theme.palette.text.secondary;

  const valueColor = isZero
    ? theme.palette.text.disabled
    : active ? accentHex : theme.palette.text.primary;
  const iconColor = isZero
    ? theme.palette.text.disabled
    : active ? accentHex : theme.palette.text.secondary;
  const labelColor = isZero ? theme.palette.text.disabled : theme.palette.text.secondary;

  return (
    <Tooltip title={tooltip} arrow placement="bottom">
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          px: 0.5,
          height: 46,
          cursor: 'help',
          opacity: isZero ? 0.55 : 1,
          transition: 'opacity 0.15s ease',
          '&:hover': { opacity: 1 },
        }}
      >
        {React.cloneElement(icon as React.ReactElement, {
          sx: { color: iconColor, fontSize: 18, flexShrink: 0 },
        })}
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: valueColor, lineHeight: 1 }}>
          {value}
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 500, fontSize: '0.72rem', color: labelColor, lineHeight: 1 }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const AutomationStatusOverview: React.FC<AutomationStatusOverviewProps> = ({
  vehicles,
  autoSearchSessions,
  autoNegoSessions,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const totalVehicles = vehicles.length;

  const vehiclesWithSearch = vehicles.filter(v => {
    const s = autoSearchSessions.get(v.id);
    return s?.autoSearchState === AutoSearchState.ON;
  }).length;

  const isNegoActive = (state?: AutoNegoState) => state === AutoNegoState.ON || state === AutoNegoState.WAITING_RESULTS || state === AutoNegoState.RESULT_TO_REVIEW;
  const vehiclesWithNego = vehicles.filter(v => isNegoActive(autoNegoSessions.get(v.id)?.autoNegoState)).length;

  const vehiclesFullyAutomated = vehicles.filter(v => {
    const searchOn = autoSearchSessions.get(v.id)?.autoSearchState === AutoSearchState.ON;
    const negoOn = isNegoActive(autoNegoSessions.get(v.id)?.autoNegoState);
    return searchOn && negoOn;
  }).length;

  const needsReview = vehicles.filter(v => autoNegoSessions.get(v.id)?.autoNegoState === AutoNegoState.RESULT_TO_REVIEW).length;
  const successfulNegos = vehicles.reduce((acc, v) => acc + (autoNegoSessions.get(v.id)?.successfulNegosCount || 0), 0);

  const automationRate = totalVehicles > 0
    ? Math.round((vehiclesFullyAutomated / totalVehicles) * 100)
    : 0;

  const progressColor: 'success' | 'warning' | 'inherit' =
    automationRate >= 80 ? 'success' : automationRate >= 50 ? 'warning' : 'inherit';

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        {/* Automation progress — the headline metric */}
        <Tooltip
          arrow
          placement="bottom"
          title={t('{{automated}} of {{total}} trucks have both auto-search and auto-nego enabled', {
            automated: vehiclesFullyAutomated,
            total: totalVehicles,
          })}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.25,
              height: 46,
              borderRadius: 1.75,
              border: '1px solid',
              borderColor: isLight ? 'rgba(15,23,42,0.1)' : 'rgba(255,255,255,0.08)',
              bgcolor: isLight ? 'rgba(248,250,252,0.92)' : 'rgba(255,255,255,0.02)',
              cursor: 'help',
              minWidth: 200,
            }}
          >
            <SmartToyIcon sx={{ color: 'primary.main', fontSize: 18 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', color: 'text.primary', lineHeight: 1 }}>
                  {t('Automation')}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: 'text.primary', lineHeight: 1 }}>
                  {vehiclesFullyAutomated}/{totalVehicles}
                  <Typography
                    component="span"
                    sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 500, fontSize: '0.72rem' }}
                  >
                    ({automationRate}%)
                  </Typography>
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={automationRate}
                color={progressColor === 'inherit' ? 'primary' : progressColor}
                sx={{
                  height: 5,
                  borderRadius: 3,
                  bgcolor: isLight ? 'rgba(15,23,42,0.06)' : 'rgba(255,255,255,0.06)',
                }}
              />
            </Box>
          </Box>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />

        {/* Inline stats — no borders, dimmed when 0 */}
        <Box sx={{ display: 'flex', gap: 1.75, flexWrap: 'wrap', alignItems: 'center' }}>
          <StatPill
            icon={<SearchIcon />}
            label={t('Auto-search')}
            value={vehiclesWithSearch}
            tooltip={t('Trucks with auto-search currently running')}
          />
          <StatPill
            icon={<HandshakeIcon />}
            label={t('Auto-nego')}
            value={vehiclesWithNego}
            tooltip={t('Trucks with auto-negotiation active (running, waiting, or needing review)')}
          />
          <StatPill
            icon={<NotificationsActiveIcon />}
            label={t('To review')}
            value={needsReview}
            tooltip={t('Negotiations awaiting your review')}
            tone="warning"
          />
          <StatPill
            icon={<EmojiEventsIcon />}
            label={t('Won')}
            value={successfulNegos}
            tooltip={t('Total negotiations won by automation')}
            tone="success"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AutomationStatusOverview;
